import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { Employee } from '../../models/employee.models';
import {
  AttendanceService,
  AttendanceRecord,
  AttendanceStatus,
} from '../../../services/attendance.service';
import { LocationService } from '../../../services/location.service';
import { ApiResponse } from '../../../shared/types';
import { Subject, takeUntil } from 'rxjs';
import {
  formatCalendarDateInTimeZone,
  formatDateInTimeZone,
  formatTimeInTimeZone,
  getDefaultShopTimezone,
  getUserTimezone,
} from '../../../shared/utils/date.util';

@Component({
  selector: 'app-attendance-records-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDatepickerModule, MatInputModule],
  templateUrl: './attendance-records-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./attendance-records-modal.component.scss'],
})
export class AttendanceRecordsModalComponent implements OnInit, OnDestroy {
  @Input() set isVisible(value: boolean) {
    this._isVisible = value;
    if (value && this.employee) {
      this.initDefaultDateRange();
      this.loadAttendanceRecords();
    }
  }
  get isVisible(): boolean {
    return this._isVisible;
  }
  @Input() set employee(value: Employee | null) {
    this._employee = value;
    if (value && this.isVisible) {
      this.initDefaultDateRange();
      this.loadAttendanceRecords();
    }
  }
  get employee(): Employee | null {
    return this._employee;
  }
  @Output() closeModal = new EventEmitter<void>();

  private _employee: Employee | null = null;
  private _isVisible = false;
  private destroy$ = new Subject<void>();

  // Date range
  startDate: Date | null = null;
  endDate: Date | null = null;
  /** Stable max selectable day for pickers (location "today"). */
  locationTodayDate: Date = new Date();

  // Attendance data
  attendanceRecords: AttendanceRecord[] = [];
  isLoading = false;
  errorMessage = '';

  // Sorting
  sortColumn: 'date' | null = null;
  sortDirection: 'asc' | 'desc' = 'desc';

  constructor(
    private attendanceService: AttendanceService,
    private locationService: LocationService
  ) {}

  /** Employee location timezone (for displaying check-in/out times). */
  get employeeTimezone(): string {
    return (
      this.locationService.getTimezoneForLocationId(this.employee?.location_id) || getUserTimezone()
    );
  }

  /**
   * Admin/shop default timezone for picker "today" and date-range bounds.
   * Prefer default shop from shop_location_state (not browser local day).
   */
  get adminShopTimezone(): string {
    return getDefaultShopTimezone() || getUserTimezone();
  }

  ngOnInit(): void {
    this.initDefaultDateRange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private refreshLocationTodayDate(): void {
    const timezone = this.adminShopTimezone;
    const ymd = formatCalendarDateInTimeZone(new Date(), timezone);
    if (!ymd) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      this.locationTodayDate = today;
      return;
    }
    const [y, m, d] = ymd.split('-').map(Number);
    this.locationTodayDate = new Date(y, m - 1, d);
  }

  private initDefaultDateRange(): void {
    this.refreshLocationTodayDate();
    const endDate = new Date(this.locationTodayDate);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 30);
    this.endDate = endDate;
    this.startDate = startDate;
  }

  onClose(): void {
    // Material DatePicker handles overlay automatically
    // No need to manually manage overlay visibility
    this.resetModalState();
    this.closeModal.emit();
  }

  onModalClick(event: Event): void {
    event.stopPropagation();
  }

  onSearch(): void {
    if (this.startDate && this.endDate) {
      this.loadAttendanceRecords();
    }
  }

  /**
   * Download the currently displayed attendance records as a CSV file and trigger a browser download.
   * @example
   * nDownloadCSV()
   * undefined
   * @returns {void} Triggers download of a CSV file containing the filtered, formatted attendance records (filename includes employee name and date range); returns nothing.
   */
  onDownloadCSV(): void {
    if (!this.attendanceRecords || this.attendanceRecords.length === 0) {
      console.warn('No attendance records to download');
      return;
    }

    // Get filtered and sorted records (same as displayed in table)
    const records = this.getFilteredRecords();

    // Helper function to escape CSV values
    const escapeCSV = (value: string): string => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      // If value contains comma, quote, or newline, wrap in quotes and escape quotes
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    // Create CSV headers
    const headers = [
      'Date',
      'Day',
      'Status',
      'Check-in',
      'Checkout',
      'Hours',
      'Break Start',
      'Break End',
      'Break Duration',
    ];

    // Create CSV rows with exact same data as shown in table
    const rows = records.map(record => {
      const breakStart = this.getBreakStart(record);
      const breakEnd = this.getBreakEnd(record);

      return [
        escapeCSV(this.formatDate(record.date)),
        escapeCSV(this.getDayOfWeek(record.date)),
        escapeCSV(this.getStatusLabel(record.status)),
        escapeCSV(this.formatTime(record.check_in || record.check_in_local || null)),
        escapeCSV(this.formatTime(record.check_out || record.check_out_local || null)),
        escapeCSV(this.formatHours(record.work_hours, record)),
        escapeCSV(this.formatBreakTime(breakStart)),
        escapeCSV(this.formatBreakTime(breakEnd)),
        escapeCSV(this.formatBreakDuration(record.break_hours, record)),
      ];
    });

    // Combine headers and rows
    const csvContent = [headers.map(escapeCSV).join(','), ...rows.map(row => row.join(','))].join(
      '\n'
    );

    // Create blob and download
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel compatibility
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    // Generate filename with employee name and date range
    const employeeName = this.getEmployeeFullName().replace(/\s+/g, '_');
    const startDateStr = this.startDate ? this.formatDateForAPI(this.startDate) : 'start';
    const endDateStr = this.endDate ? this.formatDateForAPI(this.endDate) : 'end';
    const filename = `attendance_${employeeName}_${startDateStr}_to_${endDateStr}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);
  }

  onSortDate(): void {
    if (this.sortColumn === 'date') {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = 'date';
      this.sortDirection = 'desc';
    }
    this.sortRecords();
  }

  getEmployeeFullName(): string {
    if (!this.employee) return '';
    return `${this.employee.first_name} ${this.employee.last_name}`.trim();
  }

  /**
   * Map an attendance status to a corresponding CSS badge class string.
   * @example
   * etStatusBadgeClass('present')
   * 'status-present'
   * @param {{AttendanceStatus}} {{status}} - Attendance status to map to a CSS badge class.
   * @returns {{string}} CSS class name corresponding to the provided attendance status.
   **/
  getStatusBadgeClass(status: AttendanceStatus): string {
    switch (status) {
      case 'present':
        return 'status-present';
      case 'absent':
        return 'status-absent';
      case 'late':
        return 'status-on-leave';
      case 'leave':
        return 'status-on-leave';
      case 'working':
        return 'status-working';
      default:
        return '';
    }
  }

  /**
   * Return a human-readable label for a given attendance status.
   * @example
   * etStatusLabel('present')
   * 'Present'
   * @param {{AttendanceStatus}} {{status}} - The attendance status identifier.
   * @returns {{string}} Return label corresponding to the provided status.
   **/
  getStatusLabel(status: AttendanceStatus): string {
    switch (status) {
      case 'present':
        return 'Present';
      case 'absent':
        return 'Absent';
      case 'late':
        return 'On Leave';
      case 'leave':
        return 'Leave';
      case 'working':
        return 'Working';
      default:
        return status;
    }
  }

  /**
   * Format an ISO date string into "Mon DD YYYY" (e.g., "Jan 01 2020") using a date parsed without timezone conversion.
   * @example
   * ormatDate('2020-01-01T12:00:00Z')
   * Jan 01 2020
   * @param {{string}} {{dateString}} - The ISO date string to format; if falsy returns '' and if parsing fails returns the original string.
   * @returns {{string}} Return the formatted date as "Mon DD YYYY", or '' for falsy input, or the original string if parsing fails.
   **/
  formatDate(dateString: string): string {
    if (!dateString) return '';

    const ymd =
      dateString.includes('T') && this.employeeTimezone
        ? formatCalendarDateInTimeZone(dateString, this.employeeTimezone)
        : dateString.split('T')[0];
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      return (
        formatDateInTimeZone(dateString, this.employeeTimezone, {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        }) || dateString
      );
    }

    const [year, month, day] = ymd.split('-').map(Number);
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return `${monthNames[month - 1]} ${String(day).padStart(2, '0')} ${year}`;
  }

  getDayOfWeek(dateString: string): string {
    const ymd =
      dateString.includes('T') && this.employeeTimezone
        ? formatCalendarDateInTimeZone(dateString, this.employeeTimezone)
        : dateString.split('T')[0];
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      return '';
    }
    const [year, month, day] = ymd.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getDay()];
  }

  formatTime(timeString: string | null | undefined): string {
    if (!timeString) return '-';
    const localTime = this.formatTimezoneLessTime(timeString);
    if (localTime) {
      return localTime;
    }
    return formatTimeInTimeZone(timeString, this.employeeTimezone) || '-';
  }

  /**
   * Format break time - extracts only the time portion from ISO date-time string
   * @param timeString - ISO date-time string (e.g., "2026-01-23T06:42:59.615+00:00")
   * @returns Formatted time string (e.g., "6:42 AM")
   */
  formatBreakTime(timeString: string | null | undefined): string {
    if (!timeString) return '-';
    const localTime = this.formatTimezoneLessTime(timeString);
    if (localTime) {
      return localTime;
    }
    return formatTimeInTimeZone(timeString, this.employeeTimezone) || '-';
  }

  private formatTimezoneLessTime(timeString: string | null | undefined): string | null {
    const value = String(timeString || '');
    const timePart = value.split('T')[1] || '';
    if (!timePart || /(?:Z|[+-]\d{2}:?\d{2})$/i.test(timePart)) {
      return null;
    }

    const timeMatch = value.match(/T(\d{2}):(\d{2})/);
    if (!timeMatch) {
      return null;
    }

    const hours = Number(timeMatch[1]);
    const minutes = timeMatch[2];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes} ${ampm}`;
  }

  /**
   * Formats a work hours value (string or object) or computes duration from an attendance record and returns a human-readable hours/minutes string.
   * @example
   * ormatHours("01:30:00")
   * "1h 30m"
   * @param {{any}} {{workHours}} - Work hours provided by the API; can be a string like "HH:MM:SS.ms" or an object containing hours/minutes.
   * @param {{AttendanceRecord}} {{record}} - Optional AttendanceRecord used to calculate duration from check-in/check-out when workHours is not available.
   * @returns {{string}} Return a formatted string like "1h 30m" or "-" when no valid data is available.
   **/
  formatHours(workHours: any, record?: AttendanceRecord): string {
    // Prioritize API-provided work_hours object
    if (workHours) {
      if (typeof workHours === 'string') {
        // Parse string format like "00:03:20.609" or "18:54:21.321"
        const parts = workHours.split(':');
        if (parts.length >= 2) {
          const hours = parseInt(parts[0], 10) || 0;
          const minutes = parseInt(parts[1], 10) || 0;

          if (hours > 0 && minutes > 0) {
            return `${hours}h ${minutes}m`;
          } else if (hours > 0) {
            return `${hours}h`;
          } else if (minutes > 0) {
            return `${minutes}m`;
          }
        }
        return workHours;
      }

      if (typeof workHours === 'object') {
        // Handle API format: { minutes: 23, seconds: 36, milliseconds: 229 }
        const hours = workHours.hours || 0;
        const minutes = workHours.minutes || 0;
        const formatted = this.formatHoursMinutesToString(hours, minutes);
        if (formatted) return formatted;
      }
    }

    // Fallback: Calculate hours from checkout - checkin if record is provided and API data not available
    if (record) {
      const checkIn = record.check_in || record.check_in_local;
      const checkOut = record.check_out || record.check_out_local;

      if (checkIn && checkOut) {
        const duration = this.calculateTimeDifference(checkIn, checkOut);
        return this.formatDuration(duration);
      }
    }

    return '-';
  }

  /**
   * Format break duration from an API break_hours object or compute it from an AttendanceRecord; returns '-' if unavailable.
   * @example
   * ormatBreakDuration({ hours: 1, minutes: 5 })
   * '1h 5m'
   * @param {{any}} {{breakHours}} - API break_hours object (e.g., { hours: number, minutes: number, seconds?: number }) or a preformatted duration string.
   * @param {{AttendanceRecord}} {{record}} - Optional attendance record used to compute break duration when API data is not provided.
   * @returns {{string}} Formatted break duration string (e.g. '1h 5m') or '-' when no data is available.
   **/
  formatBreakDuration(breakHours: any, record?: AttendanceRecord): string {
    // Prioritize API-provided break_hours object
    if (breakHours) {
      if (typeof breakHours === 'string') {
        return breakHours;
      }

      if (typeof breakHours === 'object' && Object.keys(breakHours).length > 0) {
        // Handle API format: { minutes: 4, seconds: 54 }
        const hours = breakHours.hours || 0;
        const minutes = breakHours.minutes || 0;
        const formatted = this.formatHoursMinutesToString(hours, minutes);
        if (formatted) return formatted;
      }
    }

    // Fallback: Calculate break duration from break end - break start if record is provided and API data not available
    if (record) {
      const breakStart = this.getBreakStart(record);
      const breakEnd = this.getBreakEnd(record);

      if (breakStart && breakEnd) {
        const duration = this.calculateTimeDifference(breakStart, breakEnd);
        return this.formatDuration(duration);
      }
    }

    return '-';
  }

  private formatHoursMinutesToString(hours: number, minutes: number): string | null {
    const totalMinutes = hours * 60 + minutes;
    const displayHours = Math.floor(totalMinutes / 60);
    const displayMinutes = totalMinutes % 60;
    if (displayHours > 0 && displayMinutes > 0) return `${displayHours}h ${displayMinutes}m`;
    if (displayHours > 0) return `${displayHours}h`;
    if (displayMinutes > 0) return `${displayMinutes}m`;
    return null;
  }

  /**
   * Calculate time difference between two time strings
   * @param startTime - Start time string (ISO format or time string)
   * @param endTime - End time string (ISO format or time string)
   * @returns Duration in milliseconds
   */
  private calculateTimeDifference(startTime: string, endTime: string): number {
    try {
      // Ensure time strings have 'Z' suffix for UTC parsing
      let startStr = startTime;
      let endStr = endTime;

      if (!startStr.endsWith('Z') && startStr.includes('T')) {
        startStr = startStr + 'Z';
      }
      if (!endStr.endsWith('Z') && endStr.includes('T')) {
        endStr = endStr + 'Z';
      }

      const start = new Date(startStr);
      const end = new Date(endStr);

      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return 0;
      }

      return end.getTime() - start.getTime();
    } catch {
      return 0;
    }
  }

  /**
   * Format duration in milliseconds to human-readable string
   * @param durationMs - Duration in milliseconds
   * @returns Formatted string like "2h 30m" or "45m" or "30s"
   */
  private formatDuration(durationMs: number): string {
    if (durationMs <= 0) return '-';

    const totalSeconds = Math.floor(durationMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else if (seconds > 0) {
      return `${seconds}s`;
    }
    return '-';
  }

  getBreakStart(record: AttendanceRecord): string | null {
    // Break start is the checkout time of the first session
    if (record.sessions && record.sessions.length > 0) {
      const firstSession = record.sessions[0];
      // Use check_out from first session, or fallback to session_check_out for legacy format
      return firstSession.check_out || firstSession.session_check_out || null;
    }
    // Fallback to break_start field if sessions are not available
    return record.break_start || null;
  }

  getBreakEnd(record: AttendanceRecord): string | null {
    // Break end is the checkin time of the last session
    if (record.sessions && record.sessions.length > 0) {
      const lastSession = record.sessions[record.sessions.length - 1];
      // Use check_in from last session, or fallback to session_check_in for legacy format
      return lastSession.check_in || lastSession.session_check_in || null;
    }
    // Fallback to break_end field if sessions are not available
    return record.break_end || null;
  }

  /**
   * Returns a sorted shallow copy of the component's attendance records, applying date-based sorting when sortColumn is 'date' and respecting sortDirection (uses parseDateWithoutTimezone to avoid timezone conversion).
   * @example
   * etFilteredRecords()
   * [{ date: '2024-01-01', employeeId: 'E123', status: 'present' }]
   * @returns {AttendanceRecord[]} An array of AttendanceRecord objects sorted according to the component's sort settings.
   **/
  getFilteredRecords(): AttendanceRecord[] {
    const records = [...this.attendanceRecords];

    // Apply sorting
    if (this.sortColumn === 'date') {
      records.sort((a, b) => {
        // Parse dates without timezone conversion for accurate sorting
        const dateA = this.parseDateWithoutTimezone(a.date);
        const dateB = this.parseDateWithoutTimezone(b.date);
        if (!dateA || !dateB) return 0;
        const timeA = dateA.getTime();
        const timeB = dateB.getTime();
        return this.sortDirection === 'asc' ? timeA - timeB : timeB - timeA;
      });
    }

    return records;
  }

  /**
   * Load attendance records for the currently selected employee between startDate and endDate, manage loading and error state, and sort the results.
   * @example
   * loadAttendanceRecords()
   * void
   * @returns {void} - No return value; updates component properties (attendanceRecords, isLoading, errorMessage) based on API response.
   */
  private loadAttendanceRecords(): void {
    if (!this.employee?.id || !this.startDate || !this.endDate) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    // Format dates as YYYY-MM-DD for the selected calendar days (location-aligned defaults / max).
    const startDateStr = this.formatDateForAPI(this.startDate);
    const endDateStr = this.formatDateForAPI(this.endDate);

    this.attendanceService
      .getEmployeeAttendanceRecords(
        this.employee.id,
        startDateStr,
        endDateStr,
        this.employeeTimezone
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ApiResponse<AttendanceRecord[]>) => {
          this.isLoading = false;
          if (response.status && response.data) {
            // API already returns records for this specific employee
            this.attendanceRecords = response.data;
            this.sortRecords();
          } else {
            this.attendanceRecords = [];
          }
        },
        error: error => {
          console.error('Error loading attendance records:', error);
          this.isLoading = false;
          this.errorMessage = 'Failed to load attendance records';
          this.attendanceRecords = [];
        },
      });
  }

  private formatDateForAPI(date: Date): string {
    // Format date in local timezone (YYYY-MM-DD) to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Sorts the component's attendanceRecords array in place based on the current sortColumn and sortDirection (handles date parsing without timezone conversion).
   * @example
   * sortRecords()
   * // undefined
   * @returns {void} No return value; the method mutates this.attendanceRecords by sorting it.
   */
  private sortRecords(): void {
    if (this.sortColumn === 'date') {
      this.attendanceRecords.sort((a, b) => {
        // Parse dates without timezone conversion for accurate sorting
        const dateA = this.parseDateWithoutTimezone(a.date);
        const dateB = this.parseDateWithoutTimezone(b.date);
        if (!dateA || !dateB) return 0;
        const timeA = dateA.getTime();
        const timeB = dateB.getTime();
        return this.sortDirection === 'asc' ? timeA - timeB : timeB - timeA;
      });
    }
  }

  trackByRecordId(index: number, record: AttendanceRecord): string {
    return record.id || index.toString();
  }

  private resetModalState(): void {
    this.attendanceRecords = [];
    this.isLoading = false;
    this.errorMessage = '';
    this.sortColumn = null;
    this.sortDirection = 'desc';
  }

  /**
   * Parse date string without timezone conversion
   * Extracts YYYY-MM-DD from ISO string and creates a Date object in local timezone
   * This prevents UTC dates like "2026-01-27T00:00:00.000Z" from being converted to previous day
   */
  private parseDateWithoutTimezone(dateString: string): Date | null {
    if (!dateString) return null;

    try {
      // Extract date part (YYYY-MM-DD) from ISO string
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);

      // Validate date components
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        return null;
      }

      // Create date in local timezone using the date components directly
      return new Date(year, month - 1, day);
    } catch (error) {
      console.warn('Error parsing date without timezone:', dateString, error);
      return null;
    }
  }
}
